// ─────────────────────────────────────────────
//  HomePage — the post-login landing ("the lobby").
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  Four doors onto the day — Board · My Rooms · Timeline · Messages — and, on a
//  full-width plaque beneath them, one onto the department: Team. This is the
//  first screen of a shift, and its whole job is to put a clinician somewhere
//  without making them read.
//
//  ⚠️ TEAM IS DELIBERATELY NOT A FIFTH TILE. The four doors are the day being
//  worked; the directory is a reference book you open, take one number out of,
//  and close. It is also what the grid can carry: a fifth equal tile lands as
//  2 + 2 + 1 and leaves an orphan under the row (see `main`'s comment on the
//  760/300 pair, which must not be widened to make five fit). Spanning the row
//  says "different kind of destination" and costs the layout nothing.
//
//  WHY IT LOOKS DIFFERENT FROM THE REST OF THE APP. The clinical-blue ground is
//  for working screens — a board read across a room, a timeline being edited.
//  This is not a working screen, so the surfaces are warm (color.lobby) while
//  the ink and the marks stay ATC navy. See the lobby palette in theme/tokens.js
//  for the contrast table; `brass` is a rule colour and never a text colour.
//
//  ⚠️ THIS IS NOW A BOOT PATH. Every clinician lands here at login, so it must
//  never render nothing. The live counts (rooms today, unread messages) are
//  DECORATION: each tile is fully usable when its hook returns empty, and the
//  page mounts no gate of its own. If you add data here, keep that property.
//
//  Who never sees it (App.js does the redirecting, not this file):
//    • Board Manager — a shared wall device whose world is the board itself.
//    • Scheduler — bounced to the scheduling module.
//    • A user with no facility — bounced to the no-access notice.
//  An Observer DOES land here but gets two tiles: they have no personal room
//  view, no messaging, and no directory. The last of those is a rules decision
//  before it is a layout one — the directory is the personal mobile number of
//  everyone in the department, and the Firestore rules name the three roles that
//  may read it (provider, anesthesiologist, admin) AND require the account to be
//  at one of this tenant's campuses — so the door is hidden to match a door that
//  would not open. ⚠️ That test is a POSITIVE allowlist shared with NavMenu and
//  the page (domain/teamDirectory.canSeeTeamDirectory), never a local
//  `!isObserver`. A door onto an empty room, or onto a permission error, is
//  worse than no door.
// ─────────────────────────────────────────────
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useMessaging } from "../context/MessagingContext";
import { useDayKey } from "../hooks/useDayKey";
import { useMyRooms } from "../hooks/useMyRooms";
import { Avatar, SignOutButton } from "../components";
import NavMenu from "../components/NavMenu";
import DirectivesCard from "../components/DirectivesCard";
import { canSeeTeamDirectory } from "../domain/teamDirectory";
import tokens from "../theme/tokens";

const { color, space, radius, font } = tokens;
const { lobby } = color;

// Which Timeline a person gets.
//
// The full chart lets you move a case; Timeline Light does not. The split is by
// what the role may already DO to a case elsewhere in the app — the same set
// that `canManageAssignments` grants — so nobody gets an editable surface here
// that the board would refuse them. CRNAs, AAs and residents read.
//
// Exported so the routing rule can be tested without mounting the page, which
// would pull in Firebase, messaging and the alert centre.
export function timelineRouteFor(auth) {
  const canEdit = !!(auth?.isDoctor || auth?.isAdmin || auth?.isAIC || auth?.isBoardManager);
  return canEdit ? "/timeline" : "/timeline-light";
}

// "Good morning" / "afternoon" / "evening" off the device clock. Deliberately
// the DEVICE's hour and not the facility's: this greets the person holding the
// screen, unlike the day key below, which addresses documents.
export function greetingFor(hour) {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

// The first word of a name, for the greeting. Falls back to a greeting with no
// name rather than to "there" or a uid, which read as a broken mail merge.
export function firstNameOf(name) {
  const first = String(name || "").trim().split(/\s+/)[0];
  return first || "";
}

// Format a "YYYY-MM-DD" day key for reading.
//
// ⚠️ `new Date("2026-08-18")` parses as UTC and prints as the 17th west of
// Greenwich. The parts are split by hand so the date shown is the date the key
// names — this page's line must agree with the board underneath it.
export function formatDayKey(key) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ""));
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric",
  });
}

export default function HomePage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { user, isObserver } = auth;
  const { unread } = useMessaging();
  const dateKey = useDayKey();
  const { myCards } = useMyRooms(user?.activeFacilityId, dateKey);

  const roomCount = Array.isArray(myCards) ? myCards.length : 0;
  const unreadCount = Number(unread) || 0;
  const name = firstNameOf(user?.name);
  const greeting = greetingFor(new Date().getHours());
  const canEditTimeline = timelineRouteFor(auth) === "/timeline";

  // The one factual line under the greeting. It answers the question a person
  // actually arrives with — am I on rooms today, and is anyone waiting on me —
  // in plain sentences. NO alarm colour: a normal shift with three rooms and two
  // unread messages is not a warning, and spending amber on it would teach
  // people to ignore amber where it means an emergency.
  const standing = (() => {
    const parts = [];
    if (roomCount === 1) parts.push("You're on 1 room today.");
    else if (roomCount > 1) parts.push(`You're on ${roomCount} rooms today.`);
    if (unreadCount === 1) parts.push("1 message is waiting.");
    else if (unreadCount > 1) parts.push(`${unreadCount} messages are waiting.`);
    return parts.join(" ");
  })();

  const doors = [
    {
      key: "board",
      eyebrow: "Every room",
      label: "Board",
      desc: "The whole day at a glance — rooms, teams and status, live.",
      icon: "📋",
      path: "/board",
      show: true,
    },
    {
      key: "rooms",
      eyebrow: "Assigned to you",
      label: "My Rooms",
      desc: "Your rooms today, with their messages, breaks and handoffs.",
      icon: "🏥",
      path: "/room",
      meta: roomCount > 0 ? `${roomCount} today` : null,
      // Observers monitor the full board and are on no rooms — same rule the
      // NavMenu already applies to this destination.
      show: !isObserver,
    },
    {
      key: "timeline",
      eyebrow: canEditTimeline ? "Read and edit" : "Read only",
      label: "Timeline",
      desc: canEditTimeline
        ? "Cases across the day, with the times you can move."
        : "Cases across the day, laid out hour by hour.",
      icon: "📊",
      path: timelineRouteFor(auth),
      show: true,
    },
    {
      key: "messages",
      eyebrow: "Rooms and people",
      label: "Messages",
      desc: "Your room channels, your groups, and everyone on today's roster.",
      icon: "💬",
      path: "/messages",
      meta: unreadCount > 0 ? `${unreadCount} unread` : null,
      // Messaging is revoked for observers in the rules, not just hidden.
      show: !isObserver,
    },
    {
      key: "team",
      eyebrow: "The whole department",
      label: "Team",
      desc: "Every anesthesiologist, CRNA, AA, SRNA, tech and surgeon, with their numbers — searchable.",
      icon: "📇",
      path: "/team",
      // `wide` spans the grid row instead of adding a fifth column-width tile.
      // See the file header: it is a layout necessity AND the honest reading of
      // what this destination is.
      wide: true,
      // ⚠️ A POSITIVE ALLOWLIST, NOT `!isObserver`. The directory is ~473
      // personal mobile numbers, and the Firestore rules name the three roles
      // that may read it — providers, anesthesiologists and admins — and
      // require membership of one of this tenant's campuses on top. Written as
      // a negative, every role invented after today would inherit this door.
      // The predicate is shared with NavMenu and the page itself so the three
      // can never drift — see domain/teamDirectory.canSeeTeamDirectory.
      show: canSeeTeamDirectory(auth),
    },
  ].filter((d) => d.show);

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <div style={s.topbarLeft}>
          <NavMenu />
        </div>
        <div style={s.topbarRight}>
          <Avatar user={user} isDoctor={auth.isDoctor} onClick={() => navigate("/profile")} />
          <SignOutButton />
        </div>
      </div>

      <main style={s.main}>
        <header style={s.welcome}>
          <div style={s.eyebrow}>{formatDayKey(dateKey)}</div>
          <h1 style={s.greeting}>
            {name ? `${greeting}, ${name}.` : `${greeting}.`}
          </h1>
          <div style={s.rule} aria-hidden="true" />
          {standing && <p style={s.standing}>{standing}</p>}
        </header>

        {/* ⚠️ WHAT THE BOARD HAS ASKED OF YOU, ABOVE THE DOORS. The reply
            controls used to exist only inside the native shell, so a clinician
            in a browser could be sent to cover a break with no way to accept or
            refuse it — while the unanswered-directive escalation nagged the
            board about them. Renders NOTHING when nothing is being asked, which
            is what keeps this boot path's "never render nothing, never gate"
            rule intact and keeps the lobby quiet on an ordinary morning. */}
        <DirectivesCard />

        <nav style={s.grid} aria-label="Where do you want to go">
          {doors.map((d) => (
            <button
              key={d.key}
              type="button"
              className="atc-plaque"
              style={d.wide ? { ...s.plaque, ...s.plaqueWide } : s.plaque}
              onClick={() => navigate(d.path)}
            >
              <span
                style={d.wide ? { ...s.medallion, ...s.medallionWide } : s.medallion}
                aria-hidden="true"
              >{d.icon}</span>
              {/* The text is its own flex column so `plaqueMeta`'s margin-top:
                  auto still pins the count to the bottom, and so a wide plaque
                  can put the medallion beside the words instead of above them
                  without a second copy of this markup. */}
              <span style={s.plaqueText}>
                <span style={s.plaqueEyebrow}>{d.eyebrow}</span>
                <span className="atc-plaque-rule" style={s.plaqueRule} aria-hidden="true" />
                <span style={s.plaqueLabel}>{d.label}</span>
                <span style={s.plaqueDesc}>{d.desc}</span>
                {d.meta && <span style={s.plaqueMeta}>{d.meta}</span>}
              </span>
            </button>
          ))}
        </nav>
      </main>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    background: lobby.linen,
    color: color.text.primary,
    fontFamily: font.family,
    display: "flex",
    flexDirection: "column",
  },
  // Matches the board's topbar geometry (56px, sticky) so moving between the
  // two does not shift the chrome — warmed to the lobby surface.
  topbar: {
    background: lobby.plaque,
    borderBottom: `1px solid ${lobby.sand}`,
    padding: "0 20px",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  topbarLeft:  { display: "flex", alignItems: "center", gap: space.sm, minWidth: 0 },
  topbarRight: { display: "flex", alignItems: "center", gap: space.sm },

  main: {
    flex: 1,
    width: "100%",
    // The measure and the tile minimum below are ONE decision: 760 is the widest
    // this can be before `auto-fit` finds room for a third 300px column and the
    // four doors break into a row of three with an orphan underneath.
    maxWidth: 760,
    margin: "0 auto",
    padding: `${space["2xl"]}px ${space.lg}px ${space["3xl"]}px`,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: space.xl,
  },

  welcome: { textAlign: "center" },
  // Signage caps: the type personality of the page. Wide tracking at a small
  // size reads as a plaque, not as a headline.
  eyebrow: {
    fontSize: 11,
    fontWeight: font.weight.bold,
    letterSpacing: "2.4px",
    textTransform: "uppercase",
    color: lobby.brassInk,
  },
  greeting: {
    margin: `${space.sm}px 0 0`,
    fontSize: 30,
    fontWeight: font.weight.semibold,
    lineHeight: font.lineHeight.tight,
    letterSpacing: "-0.3px",
    color: color.text.primary,
  },
  rule: {
    width: 56,
    height: 2,
    background: lobby.brass,
    borderRadius: radius.full,
    margin: `${space.md}px auto 0`,
  },
  standing: {
    margin: `${space.md}px 0 0`,
    fontSize: font.size.heading,
    lineHeight: font.lineHeight.normal,
    color: color.text.secondary,
  },

  grid: {
    display: "grid",
    // Two up, one up below ~660px. No media query: inline styles cannot carry
    // one, and auto-fit against a capped measure gets the same two breakpoints.
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: space.lg,
  },
  plaque: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    textAlign: "left",
    gap: space.xs,
    background: lobby.plaque,
    border: `1px solid ${lobby.sand}`,
    borderRadius: radius.xl,
    padding: `${space.lg}px ${space.lg}px ${space.md}px`,
    fontFamily: "inherit",
    color: color.text.primary,
    minHeight: 172,
    cursor: "pointer",
  },
  // The text column inside every plaque. `flex: 1` + `width: 100%` keep the
  // brass rule's hover draw and the meta line's bottom pin working exactly as
  // they did when these spans were direct children of the button.
  plaqueText: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: space.xs,
    flex: 1,
    width: "100%",
    minWidth: 0,
  },
  // ── The wide plaque (Team) ──
  // Spans the whole grid row, whether the grid is showing two columns or one,
  // so nothing is ever orphaned beside it. ⚠️ `minHeight` MUST be overridden:
  // the 172px that makes a square tile feel substantial makes a full-width band
  // look like a mistake. Laid out as a row so it reads as a banner rather than
  // a stretched card.
  plaqueWide: {
    gridColumn: "1 / -1",
    minHeight: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: space.md,
    padding: `${space.md}px ${space.lg}px`,
  },
  medallionWide: { marginBottom: 0 },
  medallion: {
    width: 46,
    height: 46,
    borderRadius: radius.full,
    background: lobby.linen,
    border: `1px solid ${color.accent.primary}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 21,
    marginBottom: space.sm,
    flexShrink: 0,
  },
  plaqueEyebrow: {
    fontSize: 10,
    fontWeight: font.weight.bold,
    letterSpacing: "1.6px",
    textTransform: "uppercase",
    color: lobby.brassInk,
  },
  plaqueRule: { height: 1, background: lobby.brass, display: "block" },
  plaqueLabel: {
    fontSize: 15,
    fontWeight: font.weight.bold,
    letterSpacing: "1.8px",
    textTransform: "uppercase",
    color: color.text.primary,
    marginTop: space.xs,
  },
  plaqueDesc: {
    fontSize: font.size.body,
    lineHeight: font.lineHeight.normal,
    color: color.text.secondary,
  },
  plaqueMeta: {
    marginTop: "auto",
    paddingTop: space.sm,
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    letterSpacing: ".4px",
    color: color.accent.primary,
  },
};
